import React, { ReactElement, useEffect, useState } from 'react';
import { StaticProvider } from '@sm/webassets/StaticContext';
import { HelmetProvider } from '@sm/webassets/Helmet';
import SurveyBasePage from '~app/components/shared/BasePage/SurveyBasePage';
import { PageLayoutProps } from './types';
import { TranslationData } from '~helpers/pages/types';

export const getLocaleMessages = async (languageLocale: string, languageCode: string): Promise<TranslationData> => {
  // Note: using allsettled inlieu of all; all rejects immediately, allsettled will wait for all promise to evaluate
  const [appMessages, webassetsMessages] = await Promise.allSettled([
    // By using webpack magic comments, it will publish the locale assets below to CDN during the build process in Team City
    import(/* webpackChunkName: "i18n/[request]" */ `../../src/locales/translated/${languageLocale}`),
    import(
      /* webpackChunkName: "i18n/webassets/[request]" */ `../../node_modules/@sm/webassets/dist/locales/translated/${languageLocale}`
    ),
  ]);
  const messages = {
    ...(appMessages.status === 'fulfilled' ? appMessages.value : []),
    ...(webassetsMessages.status === 'fulfilled' ? webassetsMessages.value : []),
  };

  return { localeMessages: messages, localeCode: languageLocale, languageCode };
};

const SurveyMonkey = ({ children, layout, staticData }: PageLayoutProps): ReactElement => {
  const { slLanguageLocale: languageLocale, languageCode } = staticData.environment;
  const [localeMessages, setlocaleMessages] = useState<TranslationData | null>(null);
  // If languageLocale is null, undefined, or 'en-US', then don't fetch locale messages and just render
  const [isFetchingLocale, setIsFetchingLocale] = useState(languageLocale != null && languageLocale !== 'en-US');

  useEffect(() => {
    const fetchLocaleMessages = async (): Promise<void> => {
      if (languageLocale != null && languageLocale !== 'en-US') {
        const messages = await getLocaleMessages(languageLocale, languageCode);
        setlocaleMessages(messages);
      }
      setIsFetchingLocale(false);
    };
    void fetchLocaleMessages();
  }, [languageLocale, languageCode]);

  if (isFetchingLocale) return <></>;

  // Wait until locale messages are fetched before rendering the JSX below, because L10nProvider in SurveyBasePage
  // expects translationData to be provided during the first render.
  return (
    <HelmetProvider>
      <StaticProvider value={staticData}>
        <SurveyBasePage translationData={localeMessages as unknown as Record<string, string>} {...layout.options}>
          {children}
        </SurveyBasePage>
      </StaticProvider>
    </HelmetProvider>
  );
};

export default SurveyMonkey;
